document.addEventListener("DOMContentLoaded", function () {
  class Back {
    constructor(id, title, variants, featuredImage, images, price, available, comparePrice) {
      this.id = id;
      this.title = title;
      this.variants = this.formatVariants(variants);
      this.featuredImage = featuredImage;
      this.images = images;
      this.price = price;
      this.available = available;
      this.comparePrice = comparePrice;
    }

    formatVariants(variants) {
      return variants.map((variant) => ({
        id: variant.id,
        baseModel: variant.option1,
        color: variant.option2,
        name: variant.name,
        available: variant.available,
        image: variant.image || this.featuredImage,
      }));
    }

    getAvailableModels() {
      return this.variants.filter((variant) => variant.available);
    }

    isAvailable() {
      return this.available && this.variants.some((variant) => variant.available);
    }

    getImageByBaseModel(baseModel) {
      const variant = this.variants.find((variant) => variant.baseModel === baseModel);
      return variant
        ? variant.image
        : "https://store.storeimages.cdn-apple.com/4982/as-images.apple.com/is/MM2A3?wid=2000&hei=2000&fmt=jpeg&qlt=95&.v=1629924876000";
    }
  }

  class Backing {
    constructor(
      variantId,
      productTitle,
      variantOption1,
      variantOption2,
      variantName,
      variantAvailable,
      productFeaturedImage,
      productImages,
      variantImage,
      variantPrice,
      variantComparePrice
    ) {
      this.id = variantId;
      this.title = productTitle;
      this.baseModel = variantOption1;
      this.color = variantOption2;
      if (this.color) {
        this.title += " " + this.color;
      }
      this.name = variantName;
      this.available = variantAvailable;
      this.featuredImage = productFeaturedImage;
      this.images = productImages;
      this.variantImage = variantImage;
      this.price = variantPrice;
      this.comparePrice = variantComparePrice;
    }
  }

  class PhoneCaseBundle {
    /**
     * Creates a PhoneCaseBundle instance.
     * @param {Object} backData - Data for the phone case (back).
     * @param {number} backData.id - Unique identifier for the phone case.
     * @param {string} backData.title - Title of the phone case.
     * @param {Array<Object>} backData.variants - Array of variant objects for different models and colors.
     * @param {string} backData.variants[].option1 - Base model of the variant (e.g., "iPhone 12").
     * @param {string} backData.variants[].option2 - Color of the variant (e.g., "Black").
     * @param {string} backData.variants[].name - Name of the variant.
     * @param {boolean} backData.variants[].available - Availability status of the variant.
     * @param {string} [backData.variants[].image] - URL of the variant's image.
     * @param {string} backData.featuredImage - Default image for the phone case.
     * @param {Array<string>} backData.images - Array of image URLs for the phone case.
     * @param {number} backData.price - Price of the phone case.
     * @param {boolean} backData.available - Overall availability of the phone case.
     * @param {number} [backData.comparePrice] - Comparison price (e.g., original price before discount).
     * @param {Array<Object>} backingsData - Array of data for the backings (additional phone case accessories).
     */
    constructor(backData, backingsData) {
      this.back = this.createBack(backData);
      this.backings = this.organizeBackingsByModel(this.createBackings(backingsData));
      this.sortBackingsAlphabetically();
    }

    /**
     * Creates a Back object from the provided data.
     * @param {Object} backData - Data for the phone case (back).
     * @returns {Back} - An instance of the Back class.
     */
    createBack(backData) {
      return new Back(
        backData.id,
        backData.title,
        backData.variants,
        backData.featured_image,
        backData.images,
        backData.price,
        backData.available,
        backData.compare_at_price
      );
    }

    /**
     * Creates Backing objects from the provided data.
     * @param {Array<Object>} backingsData - Array of data for backings.
     * @param {string} backingsData[].title - Title of the backing product.
     * @param {Array<Object>} backingsData[].variants - Array of variants for each backing product.
     * @param {number} backingsData[].variants[].id - Unique identifier for the variant.
     * @param {string} backingsData[].variants[].option1 - Base model of the variant (e.g., "iPhone 12").
     * @param {string} backingsData[].variants[].option2 - Color or pattern of the variant (e.g., "Stripes").
     * @param {string} backingsData[].variants[].name - Name of the variant.
     * @param {boolean} backingsData[].variants[].available - Availability status of the variant.
     * @param {string} [backingsData[].variants[].image] - URL of the variant's image.
     * @param {number} backingsData[].variants[].price - Price of the variant.
     * @param {number} [backingsData[].variants[].compare_at_price] - Comparison price of the variant.
     * @param {string} backingsData[].featured_image - Default image for the backing.
     * @param {Array<string>} backingsData[].images - Array of image URLs for the backing.
     * @returns {Array<Backing>} - An array of Backing instances.
     */

    createBackings(backingsData) {
      return backingsData.flatMap((backingData) => {
        return backingData.variants.map(
          (variant) =>
            new Backing(
              variant.id,
              backingData.title,
              variant.option1,
              variant.option2,
              variant.name,
              variant.available,
              backingData.featured_image,
              backingData.images,
              variant.image || backingData.featured_image,
              variant.price,
              variant.compare_at_price
            )
        );
      });
    }

    /**
     * Organizes backings by their base model.
     * @param {Array<Backing>} backings - Array of Backing instances.
     * @returns {Object} - An object where each key is a base model, and the value is an array of Backing instances.
     */
    organizeBackingsByModel(backings) {
      const organizedBackings = {};

      backings.forEach((backing) => {
        const model = backing.baseModel;
        if (!organizedBackings[model]) {
          organizedBackings[model] = [];
        }
        organizedBackings[model].push(backing);
      });

      return organizedBackings;
    }

    /**
     * Retrieves backings by the specified model.
     * @param {string} model - The base model to search for (e.g., "iPhone 12").
     * @returns {Array<Backing>} - Array of Backing instances for the specified model.
     */
    getBackingsByModel(model) {
      return this.backings[model] || [];
    }

    /**
     * Retrieves only the available backings for the specified model.
     * @param {string} model - The base model to search for (e.g., "iPhone 12").
     * @returns {Array<Backing>} - Array of available Backing instances for the specified model.
     */
    getAvailableBackingsByModel(model) {
      if (this.backings[model]) {
        return this.backings[model].filter((backing) => backing.available);
      }
      return [];
    }

    /**
     * Sorts all backings within each model alphabetically by their title, prioritizing available items.
     */
    sortBackingsAlphabetically() {
      for (const model in this.backings) {
        this.backings[model].sort((a, b) => {
          if (a.available && !b.available) return -1;
          if (!a.available && b.available) return 1;
          return a.title.localeCompare(b.title);
        });
      }
    }
  }

  class PhoneCaseBuilder {
    constructor() {
      this.nextButtons = document.querySelectorAll("[data-next-step]");
      this.prevButtons = document.querySelectorAll("[data-prev-step]");
      this.productNameEl = document.getElementById("selected-product");
      this.labelModel = document.getElementById("iphone-model-label");
      this.prevProductBtn = document.getElementById("prev-product");
      this.nextProductBtn = document.getElementById("next-product");
      this.productOptionsContainer = document.querySelector(".product-options");
      this.messageContainer = document.getElementById("message-container");
      this.toggleOptions = document.querySelectorAll(".toggle-option");
      this.noOption = document.getElementById("first-purchase-no");
      this.yesOption = document.getElementById("first-purchase-yes");
      this.selectedBaseEl = document.getElementById("selected-base");
      this.baseContainer = document.querySelector(".base");
      this.hrElement = document.getElementById("hidden");
      this.reviewImage = document.getElementById("preview-image");
      this.baseTitle = document.querySelector(".base-title");
      this.kitOptionsContainer = document.getElementById("kit-options-container");
      this.alertMessageContainer = document.getElementById("alert-message");
      this.customAlert = document.getElementById("custom-alert");
      this.reviewContainer = document.querySelector(".review-container");
      this.addToCartButton = document.querySelector(".add-to-cart");
      this.dynamicPriceElement = document.getElementById("backing-dynamic-price");
      this.selectedBackingImageContainer = document.querySelector(".selected-backing-image-container");
      this.recommendedContainer = document.getElementById("recommended-items-container");
      this.step2Container = document.querySelector(".step.step-2");
      this.backingsOptionsContainer = document.querySelector(".backings-options-container");
      this.comparePriceElement = document.getElementById("backing-compare-price");
      this.confirmationMessage = document.getElementById("confirmation-message");
      this.reviewTotalPriceEl = document.getElementById("review-total-price");
      this.imagePreviewsContainer = document.getElementById("image-previews");

      this.isCanada = window.Shopify && window.ShopifyCountry === "CA";

      this.pricingStructure = new PricingStructure(
        window.backProduct,
        window.backingsProducts,
        window.backComparePriceUSD,
        window.backComparePriceCAD,
        window.backingComparePriceUSD,
        window.backingComparePriceCAD,
        window.usdNoBackPrices,
        window.cadNoBackPrices,
        window.kits
      );
      this.priceManager = new PriceManager(this.pricingStructure);
      this.phoneCaseBundle = new PhoneCaseBundle(window.backProduct, window.backingsProducts);

      this.incompleteStepsMessage = null;

      this.baseModel = "XR";
      this.backProduct = window.backProduct || {};
      this.backingsProducts = window.backingsProducts || [];

      this.filteredBackings = this.phoneCaseBundle.getAvailableBackingsByModel(this.baseModel);
      this.backingsListEl = document.getElementById("backings-list");

      this.currentIndex = 0;
      this.selectedBase = "";
      this.selectedBackings = [];
      this.selectedKitSize = 1;
      this.isNoOptionSelected = false;
      this.optionSelected = false;
      this.selectedBaseVariantId = "";
      this.currentVariant = "";
      this.productOptions = [];
      this.availability = [];
      this.dropupContainer = null;
      this.dropupList = null;

      this.init();
    }

    init() {
      this.setupUI();
      this.bindEventListeners();
      this.initializeProductOptions();
      this.handleOptionSelection();
      this.updateKitSelection(1);
      this.deselectAllBackings();
      this.updateReviewContainerVisibility();

      if (this.productOptionsContainer) {
        this.productOptionsContainer.style.display = "flex";
      }

      if (this.labelModel) {
        this.labelModel.style.display = "none";
      }
    }

    // ---------- deselect all backings when user chaange the kit manually ----------
    deselectAllBackings() {
      document.querySelectorAll(".backing-product.selected").forEach((product) => {
        product.classList.remove("selected");
        product.removeAttribute("data-selected-variant");
        product.querySelector(".remove-backing-button")?.remove();
      });

      this.selectedBackings = [];

      const selectedBackingsCounter = document.getElementById("selected-backings");
      if (selectedBackingsCounter) {
        selectedBackingsCounter.textContent = "Backings: 0";
      }

      if (this.imagePreviewsContainer) {
        this.imagePreviewsContainer.innerHTML = "";
      }

      if (this.selectedBackingImageContainer) {
        this.selectedBackingImageContainer.innerHTML = "";

        const defaultImage = document.createElement("img");
        defaultImage.src = this.phoneCaseBundle.back.getImageByBaseModel(this.baseModel);
        defaultImage.alt = "Default iPhone Case";
        defaultImage.classList.add("selected-backing-image");
        this.selectedBackingImageContainer.appendChild(defaultImage);
      }

      if (this.reviewContainer) {
        this.reviewContainer.style.display = "none";
      }

      if (this.incompleteStepsMessage) {
        this.incompleteStepsMessage.style.display = "block";
      }

      if (this.addToCartButton) {
        this.addToCartButton.disabled = true;
      }
    }

    clearBackingsListInReview() {
      const backingsListStep3 = document.querySelector("#step-3 .backings-list");
      if (backingsListStep3) {
        backingsListStep3.innerHTML = "";
      }

      if (document.getElementById("selected-backings")) {
        document.getElementById("selected-backings").textContent = "Backings: 0";
      }
    }

    toggleRecommendedItems() {
      if (this.recommendedContainer) {
        const shouldShow = this.isNoOptionSelected && this.selectedBackings.length < 10;
        this.recommendedContainer.style.display = shouldShow ? "flex" : "none";
      }
    }

    hideRecommendedItems() {
      if (this.recommendedContainer) {
        this.recommendedContainer.style.display = "none";
      }
    }

    setupUI() {
      this.incompleteStepsMessage = document.createElement("div");

      const currentLanguage = document.documentElement.lang || "en";
      if (currentLanguage === "fr") {
        this.incompleteStepsMessage.textContent = "Veuillez compléter les étapes précédentes avant de continuer.";
      } else {
        this.incompleteStepsMessage.textContent = "Please complete the previous steps before reviewing";
      }

      this.incompleteStepsMessage.classList.add("incomplete-steps-message");

      if (this.reviewContainer && this.reviewContainer.parentNode) {
        this.reviewContainer.parentNode.insertBefore(this.incompleteStepsMessage, this.reviewContainer);
      }

      this.disableBackingsOptions();
      this.autoSelectBaseProduct();
    }

    autoSelectBaseProduct() {
      const baseProducts = document.querySelectorAll(".base-product");
      if (baseProducts.length === 1) {
        const product = baseProducts[0];
        product.classList.add("selected");
        this.selectedBase = product.dataset.productId;
        this.selectedBaseVariantId = parseInt(product.dataset.variantId, 10);
        const productTitle = product.querySelector(".product-title")
          ? product.querySelector(".product-title").textContent
          : "Selected Base";
        if (this.selectedBaseEl) this.selectedBaseEl.textContent = productTitle;
      }
      this.generateVariantList();
      this.backingsListEl = document.getElementById("backings-list");
    }

    generateVariantList() {
      const backingsOptionsContainer = document.querySelector(".backings-options");

      if (!backingsOptionsContainer) {
        console.error("Backings options container not found");
        return;
      }

      backingsOptionsContainer.innerHTML = "";
      this.filteredBackings = this.phoneCaseBundle.getAvailableBackingsByModel(this.baseModel);

      if (!this.backingsProducts || this.backingsProducts.length === 0) {
        const noBackingsMessage = document.createElement("li");
        noBackingsMessage.classList.add("backing-product");
        noBackingsMessage.textContent = "No backings available for this model.";
        backingsOptionsContainer.appendChild(noBackingsMessage);
        return;
      }

      this.filteredBackings.forEach((backing) => {
        const availableVariants = backing;
        if (availableVariants.length === 0) {
          return;
        }

        const li = document.createElement("li");
        li.classList.add("backing-product");
        li.setAttribute("data-product-id", backing.id);
        li.setAttribute("data-variants", JSON.stringify(availableVariants));
        li.setAttribute("data-price", backing.price);

        li.innerHTML = `
          <figure class="backing-figure">
            <div class="backing-image-container">
              <img 
                src="${backing.variantImage || backing.featuredImage || backing.images[0]}" 
                alt="${backing.name || backing.title}" 
                class="backing-image" 
                width="50" 
                height="50"
              >
            </div>
            <figcaption class="backing-title">${backing.name || backing.title}</figcaption>
          </figure>
        `;

        if (availableVariants.length === 0) {
          li.classList.add("sold-out");
          li.querySelector(".sold-out-badge").style.display = "block";
          li.style.opacity = "0.5";
          li.style.pointerEvents = "none";
        } else {
          li.addEventListener("click", () => {
            this.handleBackingSelection(li);
          });
        }
        backingsOptionsContainer.appendChild(li);
      });
    }

    bindEventListeners() {
      // Next buttons
      this.nextButtons.forEach((button) => {
        button.addEventListener("click", () => {
          const currentStep = button.closest(".step");
          const nextStepNumber = parseInt(button.dataset.nextStep, 10);

          // Verificação combinada: variante selecionada e opção 'Yes' ou 'No' marcada
          if (this.productNameEl.textContent === "Select your iPhone Model" || !this.currentVariant) {
            if (this.messageContainer) {
              this.messageContainer.textContent = "Please select an option and choose your iPhone model before proceeding.";
              this.messageContainer.style.color = "red";
            }
            return;
          }

          if (!this.optionSelected) {
            if (this.messageContainer) {
              const currentLanguage = document.documentElement.lang || "en";
              if (currentLanguage === "fr") {
                this.messageContainer.textContent = "Veuillez sélectionner une option avant de continuer.";
              } else {
                this.messageContainer.textContent = "Please select an option before proceeding.";
              }
              this.messageContainer.style.color = "red";
            }
            return;
          }

          if (nextStepNumber === 3 && !this.yesOption.checked) {
            if (this.selectedBackings.length === 0) {
              if (this.messageContainer) {
                this.messageContainer.textContent = "Please select at least one backing before proceeding.";
                this.messageContainer.style.color = "red";
              }
              return;
            }
          }

          if (nextStepNumber === 3 && this.yesOption.checked) {
            const selectedBackingsCount = this.selectedBackings.length;
            let minRequiredBackings;
            let maxAllowedBackings;

            switch (this.selectedKitSize) {
              case 1:
                minRequiredBackings = 1;
                maxAllowedBackings = 1;
                break;
              case 3:
                minRequiredBackings = 3;
                maxAllowedBackings = 3;
                break;
              case 5:
                minRequiredBackings = 5;
                maxAllowedBackings = 5;
                break;
              case 10:
                minRequiredBackings = 10;
                maxAllowedBackings = 10;
                break;
              default:
                minRequiredBackings = 1;
                maxAllowedBackings = 10;
            }

            if (selectedBackingsCount < minRequiredBackings) {
              if (this.customAlert && this.alertMessageContainer) {
                this.customAlert.style.display = "block";

                const currentLanguage = document.documentElement.lang || "en";
                if (currentLanguage === "fr") {
                  this.alertMessageContainer.innerHTML = `<b>Veuillez ajouter au moins ${minRequiredBackings} articles pour le kit sélectionné.</b>`;
                } else {
                  this.alertMessageContainer.innerHTML = `<b>Please add at least ${minRequiredBackings} items for the selected kit.</b>`;
                }

                this.alertMessageContainer.style.color = "#fd7ca5";
              }
              return;
            }

            if (selectedBackingsCount > maxAllowedBackings) {
              if (this.customAlert && this.alertMessageContainer) {
                this.customAlert.style.display = "block";
                this.alertMessageContainer.innerHTML = `<b>You can only select up to ${maxAllowedBackings} items for this kit.</b>`;
                this.alertMessageContainer.style.color = "#fd7ca5";
              }
              return;
            }
          }

          if (this.customAlert) this.customAlert.style.display = "none";
          this.navigateStep(currentStep, nextStepNumber);

          const nextSection = document.querySelector(`#step-${nextStepNumber}`);
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      });

      // Previous buttons
      this.prevButtons.forEach((button) => {
        button.addEventListener("click", () => {
          const currentStep = button.closest(".step");
          const prevStepNumber = parseInt(button.dataset.prevStep, 10);

          if (prevStepNumber === 1) {
            this.deselectAllBackings();
            this.productNameEl.textContent = "Select your iPhone Model";
          }

          this.navigateStep(currentStep, prevStepNumber);

          const prevSection = document.querySelector(`#step-${prevStepNumber}`);
          if (prevSection) {
            prevSection.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      });

      this.toggleOptions.forEach((option) => {
        option.addEventListener("click", () => {
          const input = option.querySelector('input[type="radio"]');
          const previousSelection = this.currentSelection;
          input.checked = true;
          this.optionSelected = true;
          this.currentSelection = input.value;

          if (this.messageContainer) {
            this.messageContainer.textContent = "";
          }

          this.toggleOptions.forEach((opt) => {
            const circle = opt.querySelector(".toggle-circle");
            circle.classList.toggle("selected", opt.querySelector('input[type="radio"]').checked);
          });

          if (previousSelection !== this.currentSelection) {
            this.deselectAllBackings();
            this.clearBackingsListInReview();
            this.blockStep3();
            this.showIncompleteStepsMessage();
          }

          if (input.value === "no") {
            this.handleNoOptionSelected();
          } else if (input.value === "yes") {
            this.handleYesOptionSelected();
          }

          this.checkFirstPurchaseOption();
          this.calculateTotalPrice();
          this.updateReviewContainerPrices();
        });
      });

      this.resetDropupState = function () {
        if (this.dropupList) {
          this.dropupList.querySelectorAll(".dropup-item").forEach((item) => {
            item.classList.remove("selected");
          });

          if (this.productNameEl) {
            this.productNameEl.textContent = "Select your iPhone Model";
          }

          this.currentVariant = null;
          this.baseModel = null;

          this.dropupList.style.display = "none";
        }
      };

      // Handle iPhone model selection
      if (this.productOptionsContainer) {
        this.productOptionsContainer.addEventListener("click", () => {
          this.enableBackingsOptions();
        });
      }

      // Kit size selection
      document.querySelectorAll(".kit-input").forEach((input) => {
        input.addEventListener("change", () => {
          this.selectedKitSize = parseInt(input.value, 10);

          this.deselectAllBackings();

          this.calculateTotalPrice();
          this.updateReviewContainerPrices();
          this.updateReviewContainerVisibility();
        });
      });

      // Backings carousel navigation
      const backingsPrevButton = document.getElementById("backings-prev");
      const backingsNextButton = document.getElementById("backings-next");
      const backingsOptions = document.querySelector(".backings-options");

      if (backingsPrevButton && backingsOptions) {
        backingsPrevButton.addEventListener("click", () => {
          backingsOptions.scrollBy({
            left: -150,
            behavior: "smooth",
          });
        });
      }

      if (backingsNextButton && backingsOptions) {
        backingsNextButton.addEventListener("click", () => {
          backingsOptions.scrollBy({
            left: 150,
            behavior: "smooth",
          });
        });
      }

      // Add to Cart button
      if (this.addToCartButton) {
        this.addToCartButton.addEventListener("click", () => {
          // Verifica se o usuário selecionou YES ou NO
          const yesOptionChecked = document.querySelector(".yes-option").checked;
          const noOptionChecked = document.querySelector(".no-option").checked;

          if (!yesOptionChecked && !noOptionChecked) {
            // Mostra mensagem de erro
            if (this.messageContainer) {
              this.messageContainer.textContent = "Please select 'Yes' or 'No' before adding to cart.";
              this.messageContainer.style.color = "red";
              this.messageContainer.style.display = "block";
            }

            // Faz scroll até o input de seleção
            const purchaseOptions = document.querySelector(".purchase-options");
            if (purchaseOptions) {
              purchaseOptions.scrollIntoView({ behavior: "smooth", block: "center" });
            }

            return; // Impede que o item seja adicionado ao carrinho
          }

          // Se uma opção foi selecionada, chama a função para adicionar ao carrinho
          this.addToCart();
        });
      }

      // Handle first purchase options
      if (this.yesOption) {
        this.yesOption.addEventListener("click", () => {});
      }

      if (this.noOption) {
        this.noOption.addEventListener("click", () => {});
      }

      this.checkFirstPurchaseOption();

      // Document click to close dropdowns
      document.addEventListener("click", (event) => this.handleDocumentClick(event));

      // Base product selection
      document.querySelectorAll(".base-product").forEach((product) => {
        product.addEventListener("click", () => {
          this.selectedBase = product.dataset.productId;
          this.selectedBaseVariantId = parseInt(product.dataset.variantId, 10);
          document.querySelectorAll(".base-product").forEach((p) => p.classList.remove("selected"));
          product.classList.add("selected");
          const productTitle = product.querySelector(".product-title")
            ? product.querySelector(".product-title").textContent
            : "Selected Base";
          if (this.selectedBaseEl) this.selectedBaseEl.textContent = productTitle;
        });
      });
    }

    disableBackingsOptions() {
      if (this.backingsOptionsContainer && this.kitOptionsContainer && this.messageContainer) {
        this.backingsOptionsContainer.style.pointerEvents = "none";
        this.backingsOptionsContainer.style.opacity = "0.5";
        this.kitOptionsContainer.style.pointerEvents = "none";
        this.kitOptionsContainer.style.opacity = "0.5";
        this.messageContainer.style.display = "block";
      }
    }

    enableBackingsOptions() {
      if (this.backingsOptionsContainer && this.kitOptionsContainer && this.messageContainer) {
        this.backingsOptionsContainer.style.pointerEvents = "auto";
        this.backingsOptionsContainer.style.opacity = "1";
        this.kitOptionsContainer.style.pointerEvents = "auto";
        this.kitOptionsContainer.style.opacity = "1";
        this.messageContainer.style.display = "none";
      }
    }

    checkFirstPurchaseOption() {
      const isModelSelected = this.productNameEl?.textContent !== "Select your iPhone Model";
      const hasValidModel = this.currentVariant && isModelSelected;
      const step2Content = document.querySelector(".step2-content");
      const step2Section = document.querySelector(".step.step-2");
      const step1Section = document.querySelector(".step.step-1");

      if (step2Content) {
        if (hasValidModel) {
          step2Content.style.display = "block";
          this.enableBackingsOptions();

          if (this.messageContainer) {
            this.messageContainer.style.display = "none";
          }
        } else {
          step2Content.style.display = "none";
          this.disableBackingsOptions();

          if (this.messageContainer && step1Section) {
            let language = document.documentElement.lang || "en";
            language = language.startsWith("fr") ? "fr" : "en";

            const messages = {
              en: "Please select an iPhone model before proceeding.",
              fr: "Veuillez sélectionner un modèle d'iPhone avant de continuer.",
            };

            this.messageContainer.textContent = messages[language];
            this.messageContainer.classList.add("error-message");
            this.messageContainer.style.fontWeight = "600";
            this.messageContainer.style.marginTop = "20px";
            this.messageContainer.style.display = "block";
            this.messageContainer.style.padding = "10px";
            this.messageContainer.style.borderRadius = "5px";
            this.messageContainer.style.color = "#fd7ca5";

            if (!step1Section.contains(this.messageContainer)) {
              step1Section.appendChild(this.messageContainer);
            }
          }
        }
      }
    }

    handleNoOptionSelected() {
      this.isNoOptionSelected = true;
      this.toggleRecommendedItems();

      const step3Title = document.querySelector("#step3-title .title-screen");
      if (step3Title) {
        step3Title.textContent = "Your custom cover(s)";
      }

      const mobileTitle = document.querySelector("#step-4 .mobile-title");

      if (mobileTitle) {
        const language = document.documentElement.lang;
        const titleText = language === "fr" ? "Votre coque personnalisée" : "Your custom case";

        mobileTitle.textContent = titleText;
      }

      if (this.kitOptionsContainer) {
        this.kitOptionsContainer.style.display = "none";
      }
      this.selectedKitSize = 10;

      if (this.labelModel) this.labelModel.style.display = "block";

      if (this.productOptionsContainer) this.productOptionsContainer.style.display = "flex";

      if (this.baseContainer) this.baseContainer.style.display = "flex";
      if (this.hrElement) this.hrElement.style.display = "block";
      if (this.baseTitle) this.baseTitle.style.display = "block";
      if (this.messageContainer) {
        this.messageContainer.textContent = "";
        this.messageContainer.style.color = "#333";
      }
    }

    handleYesOptionSelected() {
      this.isNoOptionSelected = false;
      this.hideRecommendedItems();

      if (this.kitOptionsContainer) {
        this.kitOptionsContainer.style.display = "flex";
      }

      if (this.productOptionsContainer) {
        this.productOptionsContainer.style.display = "flex";
      }

      if (this.labelModel) {
        this.labelModel.style.display = "flex";
      }

      this.selectedKitSize = 0;

      if (this.baseContainer) this.baseContainer.style.display = "flex";
      if (this.hrElement) this.hrElement.style.display = "block";
      if (this.baseTitle) this.baseTitle.style.display = "block";
      if (this.messageContainer) this.messageContainer.textContent = "";

      const mobileTitle = document.querySelector("#step-4 .mobile-title");
      if (mobileTitle) {
        const language = document.documentElement.lang;
        const titleText = language === "fr" ? "Votre coque personnalisée" : "Your custom case";

        mobileTitle.textContent = titleText;
      }

      this.optionSelected = true;
      this.updateKitSelection(1);
      this.updatePrice(1);
    }

    navigateStep(currentStep, nextStepNumber) {
      const nextStep = document.querySelector(`.step.step-${nextStepNumber}`);
      if (nextStep) {
        currentStep.classList.remove("active");
        nextStep.classList.add("active");
        this.updateProgress(nextStepNumber - 1);
      }
    }

    clearStep2Cache() {
      this.selectedBackings = [];

      document.querySelectorAll(".backing-product").forEach((product) => {
        product.classList.remove("selected");
        product.classList.remove("sold-out");
        product.removeAttribute("data-sold-out");

        const removeButton = product.querySelector(".remove-backing-button");
        if (removeButton) {
          removeButton.remove();
        }

        const badge = product.querySelector(".sold-out-badge");
        if (badge) {
          product.removeChild(badge);
        }
      });

      while (this.backingsListEl && this.backingsListEl.firstChild) {
        this.backingsListEl.removeChild(this.backingsListEl.firstChild);
      }

      if (this.imagePreviewsContainer) {
        this.imagePreviewsContainer.innerHTML = "";
      }

      if (this.selectedBackingImageContainer) {
        this.selectedBackingImageContainer.innerHTML = "";

        const defaultImage = document.createElement("img");
        defaultImage.src = this.phoneCaseBundle.back.getImageByBaseModel(this.baseModel);
        defaultImage.alt = "Default iPhone Case";
        defaultImage.classList.add("selected-backing-image");
        this.selectedBackingImageContainer.appendChild(defaultImage);
      }

      if (this.reviewImage) {
        this.reviewImage.src = this.phoneCaseBundle.back.getImageByBaseModel(this.baseModel);
      }

      if (this.customAlert) {
        this.customAlert.style.display = "none";
      }

      if (document.getElementById("selected-backings")) {
        document.getElementById("selected-backings").textContent = `Backings: 0`;
      }
    }

    clearStep2And3Cache() {
      this.clearStep2Cache();

      if (this.reviewContainer) this.reviewContainer.style.display = "none";
      this.selectedBackings = [];
      if (this.reviewImage) this.reviewImage.src = "";
      if (this.incompleteStepsMessage) this.incompleteStepsMessage.style.display = "block";
    }

    initializeProductOptions() {
      // Retrieve product options and availability from data attributes
      this.productOptions = this.productNameEl?.dataset.variants
        ? this.productNameEl.dataset.variants.split(", ").map((v) => v.trim())
        : [];

      this.availability = this.productNameEl?.dataset.availability
        ? this.productNameEl.dataset.availability.split(", ").map((avail) => avail.trim())
        : [];

      this.currentVariant = this.productOptions.length > 0 ? this.productOptions[0] : "";

      if (this.currentVariant && this.productNameEl) {
        this.productNameEl.textContent = this.currentVariant;
        this.updateBaseProduct();
      }

      if (this.productOptions.length > 0 && this.productOptions[0] !== "No variants available") {
        this.setupDropupMenu();
      } else {
        if (this.productNameEl) this.productNameEl.textContent = "No variants available";
      }
    }

    setupDropupMenu() {
      if (!this.productNameEl) return;

      const dropupContainer = document.createElement("div");
      dropupContainer.classList.add("dropup-container");

      const dropupList = document.createElement("ul");
      dropupList.classList.add("dropup-list");
      dropupContainer.appendChild(dropupList);

      const language = document.documentElement.lang;
      const selectModelText = language === "fr" ? "Sélectionnez modèle" : "Select your iPhone Model";

      this.productNameEl.textContent = selectModelText;

      if (language === "fr") {
        if (window.matchMedia("(max-width: 768px)").matches) {
          dropupList.style.transform = "translateX(-80%)";
        } else {
          dropupList.style.transform = "translateX(-110%)";
        }
      } else {
        if (window.matchMedia("(max-width: 768px)").matches) {
          dropupList.style.transform = "translateX(-80%)";
        } else {
          dropupList.style.transform = "translateX(-95%)";
        }
      }

      this.productOptions.forEach((variant, index) => {
        const listItem = document.createElement("li");
        listItem.textContent = variant;
        listItem.classList.add("dropup-item");

        if (this.availability[index] === "unavailable") {
          listItem.classList.add("disabled");
          listItem.style.pointerEvents = "none";
          listItem.style.opacity = "0.5";
        } else {
          listItem.addEventListener("click", (event) => {
            this.currentIndex = index;
            this.currentVariant = variant;
            this.productNameEl.textContent = variant;
            this.baseModel = variant;
            this.clearBackingsListInReview();
            this.checkFirstPurchaseOption();
            this.filteredBackings = this.phoneCaseBundle.getAvailableBackingsByModel(this.baseModel);
            this.clearStep2And3Cache();
            this.clearStep2Cache();
            this.generateVariantList();
            this.updateBaseProduct();
            dropupList.style.display = "none";
            event.stopPropagation();
          });
        }

        dropupList.appendChild(listItem);
      });

      this.productNameEl.parentNode.insertBefore(dropupContainer, this.productNameEl.nextSibling);

      this.dropupList = dropupList;
      this.dropupContainer = dropupContainer;
      const rightArrow = document.querySelector("#next-product");

      if (rightArrow) {
        rightArrow.addEventListener("click", (event) => {
          dropupList.style.display = dropupList.style.display === "block" ? "none" : "block";
          event.stopPropagation();
        });
      }

      document.addEventListener("click", (event) => this.handleDocumentClick(event));

      this.productNameEl.parentNode.insertBefore(dropupContainer, this.productNameEl.nextSibling);
      dropupContainer.appendChild(rightArrow);
      dropupContainer.appendChild(dropupList);

      this.dropupList = dropupList;
      this.dropupContainer = dropupContainer;
    }

    handleDocumentClick(event) {
      if (this.dropupContainer && !this.dropupContainer.contains(event.target)) {
        if (this.dropupList) this.dropupList.style.display = "none";
      }
    }

    updateBaseProduct() {
      const selectedProduct = this.productNameEl?.textContent || "";
      if (this.selectedBaseEl) this.selectedBaseEl.textContent = selectedProduct;

      // Update the base product variant ID based on the selected model
      const baseProduct = document.querySelector(".base-product");
      const variantOptions = baseProduct?.dataset.variantOptions ? JSON.parse(baseProduct.dataset.variantOptions) : [];

      // Find the matching variant ID for the selected model
      const matchingVariant = variantOptions.find((v) => v.title === selectedProduct);
      if (matchingVariant) {
        this.selectedBaseVariantId = matchingVariant.id;
      }
    }

    handleOptionSelection() {
      if (this.noOption) {
        this.noOption.addEventListener("change", () => {
          this.productNameEl.textContent = "Select your iPhone Model";
          if (this.noOption?.checked) {
            this.updatePriceBasedOnBackings();
          }
        });
      }

      if (this.yesOption) {
        this.yesOption.addEventListener("change", () => {
          this.productNameEl.textContent = "Select your iPhone Model";
          if (this.yesOption.checked) {
            this.handleYesOptionSelected();
          }
        });
      }

      document.querySelectorAll(".backing-product").forEach((product) => {
        product.addEventListener("click", () => {
          const isSelected = product.classList.contains("selected");
          this.handleBackingSelection(product);
        });
      });
    }

    updateKitSelection(kitSize) {
      this.selectedKitSize = kitSize;
      document.querySelectorAll(".kit-input").forEach((input) => {
        const isSelected = parseInt(input.value, 10) === kitSize;
        input.checked = isSelected;
        const toggleCircle = input.parentElement.querySelector(".kit-toggle-circle");
        if (toggleCircle) {
          toggleCircle.classList.toggle("selected", isSelected);
        }
      });

      this.updatePrice(kitSize);
    }

    updatePrice(kitSize) {
      const { total, comparePrice } = this.pricingStructure.getKitBackingPrice(kitSize, this.isCanada);

      if (this.dynamicPriceElement) this.dynamicPriceElement.textContent = `$${total.toFixed(2)}`;

      if (this.comparePriceElement) {
        this.comparePriceElement.textContent = `$${comparePrice.toFixed(2)}`;
      }
    }

    updatePriceBasedOnBackings() {
      this.calculateTotalPrice();
    }

    getBackingsQuantity() {
      return this.noOption.checked ? this.selectedBackings.length || 1 : this.selectedKitSize;
    }

    calculateTotalPrice() {
      const isNoOptionSelected = this.noOption.checked;

      // Calculate prices based on selection type
      const quantity = this.getBackingsQuantity();

      const { totalPrice, comparePrice } = this.priceManager.calculateTotalPrice(quantity, isNoOptionSelected, this.isCanada);

      const { totalBackingPrice } = this.priceManager.calculateBackingsPrice(quantity, isNoOptionSelected, this.isCanada);

      // Update the price elements directly
      if (this.reviewTotalPriceEl) this.reviewTotalPriceEl.textContent = `Total Price: $${totalPrice.toFixed(2)}`;

      if (this.dynamicPriceElement) this.dynamicPriceElement.textContent = `$${totalBackingPrice.toFixed(2)}`;

      if (this.comparePriceElement) this.comparePriceElement.textContent = `$${comparePrice.toFixed(2)}`;
    }

    updateReviewTotalPrice(totalPrice, comparePrice) {
      if (this.reviewTotalPriceEl) this.reviewTotalPriceEl.textContent = `Total Price: $${totalPrice.toFixed(2)}`;
    }

    updateTotalPrice(totalPrice, comparePrice) {
      if (this.dynamicPriceElement) this.dynamicPriceElement.textContent = `$${totalPrice.toFixed(2)}`;
      if (this.comparePriceElement) this.comparePriceElement.textContent = `$${comparePrice.toFixed(2)}`;
    }

    updateReviewContainerPrices() {
      let { totalBackingPrice, compareBackingPrice, backPrice } = this.calculateBackingPrices();
      const isNoOptionSelected = this.noOption.checked;
      const basePrice = isNoOptionSelected ? 0 : backPrice;
      const selectedModel = this.productNameEl.textContent;

      const reviewLeft = document.querySelector(".review-left");

      if (!isNoOptionSelected) {
        // Only display selected model if "Yes" is selected
        const modelDisplay = document.createElement("p");
        modelDisplay.classList.add("selected-model");

        const existingModel = reviewLeft.querySelector(".selected-model");
        if (existingModel) {
          existingModel.replaceWith(modelDisplay);
        } else {
          reviewLeft.insertBefore(modelDisplay, reviewLeft.firstChild);
        }
      } else {
        // Remove the selected model display if it exists
        const existingModel = reviewLeft.querySelector(".selected-model");
        if (existingModel) {
          existingModel.remove();
        }
      }

      // Calculate totalBackingPrice
      if (isNoOptionSelected) {
        const quantity = this.selectedBackings.length || 1;
        const { total, comparePrice } = this.pricingStructure.getNoBackBackingPrice(quantity, this.isCanada);
        totalBackingPrice = total;
        compareBackingPrice = comparePrice;
        backPrice = 0;
      } else {
        const kitSize = this.selectedKitSize;
        const pricingTiers = this.pricingStructure.getKitBackingPrice(kitSize, this.isCanada);
        totalBackingPrice = pricingTiers ? pricingTiers.total : 0;
        compareBackingPrice = pricingTiers ? pricingTiers.comparePrice : 0;
        backPrice = pricingTiers ? pricingTiers.backPrice : 0;
      }

      const totalPrice = backPrice + totalBackingPrice;
      const language = document.documentElement.lang;
      const totalPriceLabel = language === "fr" ? "Prix total" : "Total Price";

      if (this.reviewTotalPriceEl) {
        this.reviewTotalPriceEl.textContent = `${totalPriceLabel}: $${totalPrice.toFixed(2)}`;
      }

      if (this.dynamicPriceElement) {
        this.dynamicPriceElement.textContent = `$${totalBackingPrice.toFixed(2)}`;
      }

      if (this.comparePriceElement) {
        this.comparePriceElement.textContent = `$${compareBackingPrice.toFixed(2)}`;
      }
    }

    calculateBackingPrices() {
      const isNoOptionSelected = this.noOption.checked;
      let totalBackingPrice = 0;
      let compareBackingPrice = 0;
      let backPrice = 0;

      const quantity = this.selectedBackings.length || 1;
      const kitSize = this.selectedKitSize;
      const isCanada = this.isCanada;

      const pricingDetails = isNoOptionSelected
        ? this.pricingStructure.getNoBackBackingPrice(quantity, isCanada)
        : this.pricingStructure.getKitBackingPrice(kitSize, isCanada);

      totalBackingPrice = pricingDetails.total;
      compareBackingPrice = pricingDetails.comparePrice;
      backPrice = pricingDetails.backPrice;

      return { totalBackingPrice, compareBackingPrice, backPrice };
    }

    updateReviewContainerVisibility() {
      if (this.selectedBackings.length > 0) {
        if (this.reviewContainer) this.reviewContainer.style.display = "flex";
        if (this.incompleteStepsMessage) this.incompleteStepsMessage.style.display = "none";
        if (this.addToCartButton) this.addToCartButton.disabled = false;
      } else {
        if (this.reviewContainer) this.reviewContainer.style.display = "none";
        if (this.incompleteStepsMessage) this.incompleteStepsMessage.style.display = "block";
        if (this.addToCartButton) this.addToCartButton.disabled = true;
        const existingAlert = document.querySelector(".model-mismatch-alert");
        if (existingAlert) {
          existingAlert.remove();
        }
      }
    }

    // Start the backings selection and everything related to it
    handleBackingSelection(productli) {
      const maximumAllowedBackings = 10;

      const variantData = JSON.parse(productli.getAttribute("data-variants"));

      if (productli.getAttribute("data-sold-out") === "true") {
        alert("This product is sold out and cannot be selected.");
        return;
      }

      const backingVariantId = variantData.id;

      const backingTitle = variantData.title;
      const backingImageSource = variantData.variantImage || variantData.featuredImage;
      const backingPrice = parseFloat(variantData.price);
      const backingIPhoneModel = variantData.baseModel || "";

      const isBackingAlreadySelected = this.selectedBackings.some((backing) => backing.variantId === backingVariantId);

      if (isBackingAlreadySelected) {
        this.selectedBackings = this.selectedBackings.filter((backing) => backing.variantId !== backingVariantId);
        productli.classList.remove("selected");
        productli.querySelector(".selected-variant-display")?.remove();
        productli.removeAttribute("data-selected-variant");
        productli.querySelector(".remove-backing-button")?.remove();
        document.querySelector(`#backings-list li[data-variant-id="${backingVariantId}"]`)?.remove();
        document.querySelector(`#image-previews img.thumbnail[data-variant-id="${backingVariantId}"]`)?.remove();

        if (this.selectedBackings.length === 0) {
          this.reviewImage.src = this.phoneCaseBundle.back.getImageByBaseModel(this.baseModel);
          this.clearRemoveButtons();
          this.optionSelected = true;
          this.updateKitSelection(1);
          this.updatePrice(1);
          this.updateReviewContainerPrices();
        }
      } else {
        if (this.selectedBackings.length >= maximumAllowedBackings) {
          const language = document.documentElement.lang;
          const message =
            language === "fr"
              ? `<b>Vous ne pouvez sélectionner que jusqu'à ${maximumAllowedBackings} supports.</b>`
              : `<b>You can only select up to ${maximumAllowedBackings} backings.</b>`;

          this.alertMessageContainer.innerHTML = message;
          this.alertMessageContainer.style.color = "#fd7ca5";
          this.customAlert.style.width = "";
          this.customAlert.style.display = "block";

          return;
        } else {
          this.customAlert.style.display = "none";
        }

        this.selectedBackings.push({
          variantId: backingVariantId,
          title: backingTitle,
          imageSrc: backingImageSource,
          price: backingPrice,
          iPhoneModel: this.baseModel,
          backingIPhoneModel: backingIPhoneModel,
        });

        productli.classList.add("selected");

        let removeButtonElement = productli.querySelector(".remove-backing-button");

        const language = document.documentElement.lang;

        const removeText = language === "fr" ? "Supprimer" : "Remove";

        if (!removeButtonElement) {
          removeButtonElement = document.createElement("button");
          removeButtonElement.classList.add("remove-backing-button");
          removeButtonElement.textContent = removeText;
          removeButtonElement.style.marginTop = "5px";
          removeButtonElement.style.cursor = "pointer";

          removeButtonElement.addEventListener("click", (event) => {
            event.stopPropagation();
            this.handleBackingSelection(productli);

            if (this.selectedBackings.length === 0) {
              removeButtonElement.remove();
            }
          });

          productli.appendChild(removeButtonElement);
        }

        const thumbnailElement = document.createElement("img");
        thumbnailElement.src = backingImageSource;
        thumbnailElement.alt = backingTitle;
        thumbnailElement.classList.add("thumbnail");
        thumbnailElement.style.cursor = "pointer";
        thumbnailElement.setAttribute("data-variant-id", backingVariantId);

        this.imagePreviewsContainer.appendChild(thumbnailElement);

        if (this.selectedBackings.length === 1) {
          thumbnailElement.classList.add("selected-thumbnail");
          if (this.reviewImage) this.reviewImage.src = backingImageSource;
        }

        thumbnailElement.addEventListener("click", () => {
          if (this.reviewImage) this.reviewImage.src = thumbnailElement.src;
          document.querySelectorAll(".thumbnail").forEach((thumb) => thumb.classList.remove("selected-thumbnail"));
          thumbnailElement.classList.add("selected-thumbnail");
        });

        const listItemElement = document.createElement("li");
        listItemElement.setAttribute("data-variant-id", backingVariantId);
        listItemElement.className = "backing-list-item";

        const textContainerElement = document.createElement("div");
        textContainerElement.classList.add("backing-text-container");

        const nameElement = document.createElement("span");
        nameElement.classList.add("backing-name");
        nameElement.textContent = backingTitle;

        const variantElement = document.createElement("span");
        variantElement.classList.add("backing-variant");

        const priceElement = document.createElement("span");
        priceElement.classList.add("backing-price");

        textContainerElement.appendChild(nameElement);
        textContainerElement.appendChild(variantElement);
        textContainerElement.appendChild(priceElement);

        const imgElement = document.createElement("img");
        imgElement.classList.add("backing-image-3");
        imgElement.src = backingImageSource;
        imgElement.alt = backingTitle;
        imgElement.width = 50;
        imgElement.height = 50;

        listItemElement.appendChild(textContainerElement);
        listItemElement.appendChild(imgElement);

        const backingsListStep3 = document.querySelector("#step-3 .backings-list");
        if (backingsListStep3) {
          backingsListStep3.appendChild(listItemElement);
        }

        this.customAlert.style.display = "none";
      }

      if (document.getElementById("selected-backings")) {
        const language = document.documentElement.lang;
        const backingLabel = language === "fr" ? "Coques" : "Backings";

        document.getElementById("selected-backings").textContent = `${backingLabel}: ${this.selectedBackings.length}`;
      }

      this.updateBackingsMessage();
      this.updateSelectedBackingImageContainer();
      this.handleConfettiAndKit();
      this.updateReviewContainerHeight();
      this.updateReviewContainerVisibility();
      this.calculateTotalPrice();
      this.updateReviewContainerPrices();
    }

    clearRemoveButtons() {
      document.querySelectorAll(".remove-backing-button").forEach((button) => {
        button.remove();
      });
    }

    attachClickEventToListItem(listItem, backingImageSrc) {
      listItem.addEventListener("click", () => {
        if (this.reviewImage) this.reviewImage.src = backingImageSrc;
      });
    }

    updateBackingsMessage() {
      const backingsList = this.backingsListEl;
      if (this.selectedBackings.length === 0) {
        const noBackingMessage = document.createElement("span");
        noBackingMessage.id = "no-backing-message";
        noBackingMessage.textContent = "";
        backingsList.appendChild(noBackingMessage);
      } else {
        const existingMessage = document.getElementById("no-backing-message");
        if (existingMessage) {
          existingMessage.remove();
        }
      }
    }

    updateSelectedBackingImageContainer() {
      this.selectedBackingImageContainer.innerHTML = "";

      if (this.selectedBackings.length === 0) {
        const defaultImage = document.createElement("img");
        defaultImage.src = this.phoneCaseBundle.back.getImageByBaseModel(this.baseModel);
        defaultImage.alt = "Default iPhone Case";
        defaultImage.classList.add("selected-backing-image");
        this.selectedBackingImageContainer.appendChild(defaultImage);
      } else {
        this.selectedBackings.forEach((backing) => {
          const img = document.createElement("img");
          img.src = backing.imageSrc;
          img.alt = backing.title;
          img.classList.add("selected-backing-image");
          this.selectedBackingImageContainer.appendChild(img);
        });
      }
    }

    updatePreviewThumbnails() {
      this.imagePreviewsContainer.innerHTML = "";

      const backingsList = this.backingsListEl.querySelectorAll("li");

      this.selectedBackings.forEach((backing, index) => {
        const isInBackingsList = Array.from(backingsList).some(
          (item) => parseInt(item.getAttribute("data-variant-id")) === backing.variantId
        );

        if (isInBackingsList) {
          const thumbnail = document.createElement("img");
          thumbnail.src = backing.imageSrc;
          thumbnail.alt = backing.title;
          thumbnail.classList.add("thumbnail");
          thumbnail.style.width = "60px";
          thumbnail.style.cursor = "pointer";

          if (index === 0) {
            thumbnail.classList.add("selected-thumbnail");
            if (this.reviewImage) this.reviewImage.src = backing.imageSrc;
          }

          thumbnail.addEventListener("click", () => {
            if (this.reviewImage) this.reviewImage.src = thumbnail.src;
            document.querySelectorAll(".thumbnail").forEach((thumb) => thumb.classList.remove("selected-thumbnail"));
            thumbnail.classList.add("selected-thumbnail");
          });

          this.imagePreviewsContainer.appendChild(thumbnail);
        }
      });
    }

    toggleDeleteButtons() {
      const deleteButtons = document.querySelectorAll(".delete-button");
      deleteButtons.forEach((button) => {
        button.style.display = this.selectedBackings.length > 1 ? "none" : "none";
      });
    }

    // ------------- Kit Flow Functions -------------
    handleConfettiAndKit() {
      const noOptionSelected = this.noOption.checked;
      const yesOptionSelected = this.yesOption.checked;
      let currentBackingsCount = this.selectedBackings.length;
      let desiredKitSize;

      if (!this.manualKitSelection) {
        this.manualKitSelection = false;
      }

      const manualKitButtons = document.querySelectorAll(".kit-toggle-option input");
      manualKitButtons.forEach((button) => {
        button.addEventListener("click", () => {
          this.manualKitSelection = true;
          this.selectedKitSize = parseInt(button.value, 10);

          this.deselectAllBackings();
          this.clearBackingsListInReview();

          const language = document.documentElement.lang;

          const messages = {
            en: {
              kit3: "<b>You've unlocked the 3 kit option 🤩</b>",
              kit5: "<b>You've unlocked the 5 kit option 🤪</b>",
              kit10: "<b>You've unlocked the 10 kit option 😎</b>",
            },
            fr: {
              kit3: "<b>Vous avez débloqué l'option 3 kits 🤩</b>",
              kit5: "<b>Vous avez débloqué l'option 5 kits 🤪</b>",
              kit10: "<b>Vous avez débloqué l'option 10 kits 😎</b>",
            },
          };

          const selectedMessages = language === "fr" ? messages.fr : messages.en;

          switch (this.selectedKitSize) {
            case 3:
              this.alertMessageContainer.innerHTML = selectedMessages.kit3;
              break;
            case 5:
              this.alertMessageContainer.innerHTML = selectedMessages.kit5;
              break;
            case 10:
              this.alertMessageContainer.innerHTML = selectedMessages.kit10;
              break;
          }

          if (this.customAlert) {
            this.customAlert.style.display = "block";
            this.customAlert.style.color = "#fd7ca5";
          }
        });
      });

      if (currentBackingsCount === 0) {
        const kit3Label = document.querySelector('label.kit-toggle-option input[value="3"]')?.parentNode;
        const kit5Label = document.querySelector('label.kit-toggle-option input[value="5"]')?.parentNode;
        const kit10Label = document.querySelector('label.kit-toggle-option input[value="10"]')?.parentNode;

        if (kit3Label) kit3Label.classList.remove("bounce");
        if (kit5Label) kit5Label.classList.remove("bounce");
        if (kit10Label) kit10Label.classList.remove("bounce");

        let language = document.documentElement.lang || "en";
        language = language.startsWith("fr") ? "fr" : "en";

        const messages = {
          en: "<b>Please select at least one phone cover to proceed.</b>",
          fr: "<b>Veuillez sélectionner au moins une coque pour continuer.</b>",
        };

        this.alertMessageContainer.innerHTML = messages[language];

        if (this.customAlert) {
          this.customAlert.style.display = "block";
          this.customAlert.style.color = "#fd7ca5";
        }

        this.optionSelected = true;
        this.updateKitSelection(1);
        this.updatePrice(1);
        return;
      }

      if (noOptionSelected) {
        const language = document.documentElement.lang;

        const messages = {
          en: {
            1: "<b>1 cover added! Add more phone covers to save more 💸</b>",
            2: "<b>2 covers added! You have amazing taste 💋</b>",
            3: "<b>3 covers! Add more for more savings 🩷</b>",
            4: "<b>4 covers added! Love what you chose 🤩</b>",
            5: "<b>5 covers added! Keep adding for more savings 👏</b>",
            6: "<b>6 covers added! You slay bestie 💅</b>",
            7: "<b>7 covers added! Love your style 👛</b>",
            8: "<b>8 covers added! Add 2 more to cart for huge savings 😱</b>",
            9: "<b>9 covers added! Add 1 more to cart for ultimate saving 🎉</b>",
            10: "<b>10 covers added! Maximum savings unlocked 🏆</b>",
            overLimit: "<b>You've exceeded the limit of 10 covers.</b>",
          },
          fr: {
            1: "<b>1 coque ajoutée ! Ajoutez-en plus pour économiser davantage 💸</b>",
            2: "<b>2 coques ajoutées ! Vous avez un goût incroyable 💋</b>",
            3: "<b>3 coques ! Ajoutez-en plus pour encore plus d'économies 🩷</b>",
            4: "<b>4 coques ajoutées ! J'adore ce que vous avez choisi 🤩</b>",
            5: "<b>5 coques ajoutées ! Continuez à en ajouter pour plus d'économies 👏</b>",
            6: "<b>6 coques ajoutées ! Vous êtes au top 💅</b>",
            7: "<b>7 coques ajoutées ! J'adore votre style 👛</b>",
            8: "<b>8 coques ajoutées ! Ajoutez-en 2 de plus pour des économies énormes 😱</b>",
            9: "<b>9 coques ajoutées ! Ajoutez-en 1 de plus pour des économies ultimes 🎉</b>",
            10: "<b>10 coques ajoutées ! Économies maximales débloquées 🏆</b>",
            overLimit: "<b>Vous avez dépassé la limite de 10 coques.</b>",
          },
        };

        const selectedMessages = language === "fr" ? messages.fr : messages.en;

        switch (currentBackingsCount) {
          case 1:
            this.alertMessageContainer.innerHTML = selectedMessages[1];
            break;
          case 2:
            this.alertMessageContainer.innerHTML = selectedMessages[2];
            break;
          case 3:
            this.alertMessageContainer.innerHTML = selectedMessages[3];
            break;
          case 4:
            this.alertMessageContainer.innerHTML = selectedMessages[4];
            break;
          case 5:
            this.alertMessageContainer.innerHTML = selectedMessages[5];
            break;
          case 6:
            this.alertMessageContainer.innerHTML = selectedMessages[6];
            break;
          case 7:
            this.alertMessageContainer.innerHTML = selectedMessages[7];
            break;
          case 8:
            this.alertMessageContainer.innerHTML = selectedMessages[8];
            break;
          case 9:
            this.alertMessageContainer.innerHTML = selectedMessages[9];
            break;
          case 10:
            this.alertMessageContainer.innerHTML = selectedMessages[10];
            this.triggerConfetti();
            break;
          default:
            if (currentBackingsCount > 10) {
              this.alertMessageContainer.innerHTML = selectedMessages.overLimit;
            }
        }

        if (this.customAlert) {
          this.customAlert.style.display = "block";
          this.customAlert.style.color = "#fd7ca5";
        }
        return;
      }

      if (yesOptionSelected) {
        if (currentBackingsCount >= 6) {
          desiredKitSize = 10;
        } else if (currentBackingsCount >= 4) {
          desiredKitSize = 5;
        } else if (currentBackingsCount >= 2) {
          desiredKitSize = 3;
        } else {
          desiredKitSize = 1;
        }

        if (!this.manualKitSelection || desiredKitSize > this.selectedKitSize) {
          if (desiredKitSize !== this.selectedKitSize) {
            if (desiredKitSize > this.selectedKitSize && desiredKitSize !== 1) {
              this.triggerConfetti();
            }
            this.updateKitSelection(desiredKitSize);

            const currentLanguage = document.documentElement.lang || "en";

            switch (desiredKitSize) {
              case 3:
                this.alertMessageContainer.innerHTML =
                  currentLanguage === "fr"
                    ? "<b>Vous avez débloqué l'option de 3 kits 🤩</b>"
                    : "<b>You've unlocked the 3 kit option 🤩</b>";
                break;
              case 5:
                this.alertMessageContainer.innerHTML =
                  currentLanguage === "fr"
                    ? "<b>Vous avez débloqué l'option de 5 kits 🤪</b>"
                    : "<b>You've unlocked the 5 kit option 🤪</b>";
                break;
              case 10:
                this.alertMessageContainer.innerHTML =
                  currentLanguage === "fr"
                    ? "<b>Vous avez débloqué l'option de 10 kits 😎</b>"
                    : "<b>You've unlocked the 10 kit option 😎</b>";
                break;
              default:
                break;
            }

            if (this.customAlert) {
              this.customAlert.style.display = "block";
              this.customAlert.style.color = "#fd7ca5";
            }
          }
          this.manualKitSelection = false;
        }

        const currentLanguage = document.documentElement.lang || "en";

        const kit3Label = document.querySelector('label.kit-toggle-option input[value="3"]')?.parentNode;
        if (this.selectedKitSize === 1 && currentBackingsCount === 1) {
          if (kit3Label) kit3Label.classList.add("bounce");
          this.alertMessageContainer.innerHTML =
            currentLanguage === "fr"
              ? "<b>Ajoutez plus pour débloquer le kit de 3 ! Des économies supplémentaires vous attendent 🤩</b>"
              : "<b>Add more to unlock the 3 Kit! Bigger savings await 🤩</b>";
        } else if (kit3Label) {
          kit3Label.classList.remove("bounce");
        }

        const kit5Label = document.querySelector('label.kit-toggle-option input[value="5"]')?.parentNode;
        if (this.selectedKitSize === 3 && currentBackingsCount === 3) {
          if (kit5Label) kit5Label.classList.add("bounce");
          this.alertMessageContainer.innerHTML =
            currentLanguage === "fr"
              ? "<b>Ajoutez plus pour débloquer le kit de 5 ! Augmentez vos économies 🤪</b>"
              : "<b>Add more to unlock the 5 Kit! Boost your savings 🤪</b>";
        } else if (kit5Label) {
          kit5Label.classList.remove("bounce");
        }

        const kit10Label = document.querySelector('label.kit-toggle-option input[value="10"]')?.parentNode;
        if (this.selectedKitSize === 5 && currentBackingsCount === 5) {
          if (kit10Label) kit10Label.classList.add("bounce");
          this.alertMessageContainer.innerHTML =
            currentLanguage === "fr"
              ? "<b>Ajoutez plus pour débloquer le kit de 10 ! Maximisez vos économies 😱</b>"
              : "<b>Add more to unlock the 10 Kit! Max out your savings 😱</b>";
        } else if (kit10Label) {
          kit10Label.classList.remove("bounce");
        }

        if (this.customAlert) {
          this.customAlert.style.display = "block";
          this.customAlert.style.color = "#fd7ca5";
        }
      }
    }

    triggerConfetti() {
      confetti({
        particleCount: 150,
        spread: 60,
        origin: { y: 0.6 },
        colors: ["#fd7ca5", "#ffb4cf", "#cf4470", "#FF69B4"],
        shapes: ["circle", "square"],
        gravity: 1,
        scalar: 1.2,
      });
    }

    updateReviewContainerHeight() {
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      const backingsCount = this.selectedBackings.length;
      const reviewContainer = this.reviewContainer;

      const isNoOptionSelected = document.getElementById("first-purchase-no").checked;

      if (isMobile && reviewContainer) {
        if (isNoOptionSelected) {
        } else {
        }
      } else if (reviewContainer) {
        reviewContainer.style.height = "";
      }
    }

    attachAddToReviewListeners() {
      document.querySelectorAll(".add-to-review").forEach((button) => {
        button.addEventListener("click", () => {
          const variantId = parseInt(button.dataset.variantId, 10);
          const product = document.querySelector(`.backing-product[data-variant-id="${variantId}"]`);

          this.handleBackingSelection(product);

          button.disabled = true;
          button.textContent = "Added";
          button.classList.add("disabled-button");
        });
      });
    }

    clearBackingSelections() {
      this.selectedBackings = [];
      if (this.imagePreviewsContainer) this.imagePreviewsContainer.innerHTML = "";
      if (this.reviewImage) this.reviewImage.src = "";
      if (this.backingsListEl) this.backingsListEl.innerHTML = "";
      if (document.getElementById("selected-backings")) document.getElementById("selected-backings").textContent = "Covers: 0";
    }

    addToCart() {
      if (this.yesOption.checked) {
        const selectedBackingsCount = this.selectedBackings.length;
        let minRequiredBackings;
        let maxAllowedBackings;

        switch (this.selectedKitSize) {
          case 1:
            minRequiredBackings = 1;
            maxAllowedBackings = 1;
            break;
          case 3:
            minRequiredBackings = 3;
            maxAllowedBackings = 3;
            break;
          case 5:
            minRequiredBackings = 5;
            maxAllowedBackings = 5;
            break;
          case 10:
            minRequiredBackings = 10;
            maxAllowedBackings = 10;
            break;
          default:
            minRequiredBackings = 1;
            maxAllowedBackings = 10;
        }

        if (selectedBackingsCount < minRequiredBackings) {
          this.navigateToStep(2);
          this.displayCustomAlert(`Please add at least ${minRequiredBackings} items for the selected kit.`);
          return;
        }

        if (selectedBackingsCount > maxAllowedBackings) {
          this.navigateToStep(2);
          this.displayCustomAlert(`You can only select up to ${maxAllowedBackings} items for this kit.`);
          return;
        }
      }

      const items = [];
      const isNoSelected = this.noOption.checked;
      const selectedModel = this.productNameEl.textContent;

      if (isNoSelected) {
        this.selectedBackings.forEach((backing) => {
          items.push({
            id: backing.variantId,
            quantity: 1,
          });
        });
      } else {
        const baseProduct = document.querySelector(".base-product");
        const variantOptions = baseProduct.dataset.variantOptions ? JSON.parse(baseProduct.dataset.variantOptions) : [];

        // Find the matching variant ID for the selected model
        const matchingVariant = variantOptions.find((v) => v.title === selectedModel);
        if (matchingVariant) {
          this.selectedBaseVariantId = matchingVariant.id;
        }

        if (this.selectedBaseVariantId) {
          items.push({
            id: this.selectedBaseVariantId,
            quantity: 1,
          });
        }
        this.selectedBackings.forEach((backing) => {
          items.push({
            id: backing.variantId,
            quantity: 1,
          });
        });
      }

      this.showLoadingState(true);
      if (window.Tapcart.isInitialized) {
        const params = {
          lineItems: items.map((item) => ({
            variantId: item.id.toString(),
            quantity: item.quantity,
          })),
        };
        window.Tapcart.actions.addToCart({ ...params });
        setTimeout(() => {
          window.location.reload();
          window.scrollTo({
            top: 0,
            behavior: "instant",
          });
          window.Tapcart.action("screen/open", {
            destination: {
              type: "internal",
              url: "/cart?",
            },
          });
        }, 700);
      } else {
        fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items }),
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then((errorData) => {
                throw errorData;
              });
            }
            return response;
          })
          .then((response) => {
            window.refreshCartContents(response).then(() => {
              this.openOffcanvasCart();
            });
            this.resetSelections();
          })
          .catch((error) => {
            console.error("Error adding items to cart:", error);
            alert(`Error: ${error.description || "An unexpected error occurred."}`);
          })
          .finally(() => {
            this.showLoadingState(false);
          });
      }
    }

    showLoadingState(isLoading) {
      if (isLoading) {
        this.addToCartButton.textContent = "Adding...";
        this.addToCartButton.disabled = true;
      } else {
        this.addToCartButton.textContent = "Add to Cart";
        this.addToCartButton.disabled = false;
      }
    }

    openOffcanvasCart() {
      const offcanvasCart = document.querySelector("#offcanvas-cart");
      if (offcanvasCart) {
        const offcanvasInstance = bootstrap.Offcanvas.getOrCreateInstance(offcanvasCart);
        offcanvasInstance.show();
      } else {
        console.warn("Offcanvas cart element not found.");
      }
    }

    resetSelections() {
      this.selectedBase = "";
      this.selectedBackings = [];
      this.selectedBaseVariantId = "";
      if (this.selectedBaseEl) this.selectedBaseEl.textContent = "";
      if (this.backingsListEl) this.backingsListEl.innerHTML = "";
      if (document.getElementById("selected-backings")) document.getElementById("selected-backings").textContent = "Backings: 0";
      if (this.reviewImage) this.reviewImage.src = "";

      document.querySelectorAll(".backing-product").forEach((product) => {
        product.classList.remove("selected");
        product.classList.remove("sold-out");
        product.removeAttribute("data-sold-out");
        const badge = product.querySelector(".sold-out-badge");
        if (badge) {
          product.removeChild(badge);
        }
      });

      this.updateKitSelection(1);
      this.updatePrice(1);

      const modelMismatchAlert = document.getElementById("model-mismatch-alert");
      if (modelMismatchAlert) {
        modelMismatchAlert.style.display = "none";
      }
      const step1 = document.getElementById("step-1");
      if (step1) step1.style.display = "none";
      const step2 = document.querySelector(".step.step-2");
      if (step2) step2.style.display = "none";
      const stepNav3 = document.querySelector(".step-navigation-3");
      if (stepNav3) stepNav3.style.display = "none";
      if (this.reviewContainer) this.reviewContainer.style.display = "none";
      if (this.addToCartButton) this.addToCartButton.style.display = "none";
      const progressIndicator = document.querySelector(".progress-indicator");
      if (progressIndicator) progressIndicator.style.display = "none";
      const step3Title = document.getElementById("step3-title");
      if (step3Title) step3Title.style.display = "none";
      document.querySelectorAll(".progress-step").forEach((step) => (step.style.display = "none"));
      const backStep2 = document.getElementById("back-step2");
      if (backStep2) backStep2.style.display = "none";
      const recommendedItemsBar = document.getElementById("recommended-items-bar");
      if (recommendedItemsBar) recommendedItemsBar.style.display = "none";

      const step4 = document.getElementById("step-4");
      if (step4) step4.style.display = "none";

      if (this.confirmationMessage) {
        const language = document.documentElement.lang;

        if (language === "fr") {
          this.confirmationMessage.innerHTML = `
            Votre coque personnalisée est en route 🛍️✨
            <br>
            <a href="/collections/best-sellers" id="continue-shopping-btn">Continuer vos achats</a>
          `;
        } else {
          this.confirmationMessage.innerHTML = `
            Your custom case is on the way 🛍️✨
            <br>
            <a href="/collections/best-sellers" id="continue-shopping-btn">Continue Shopping</a>
          `;
        }

        this.confirmationMessage.style.display = "block";
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    navigateToStep(stepNumber) {
      document.querySelectorAll(".step").forEach((step) => {
        step.classList.remove("active");
      });

      const targetStep = document.querySelector(`.step.step-${stepNumber}`);
      if (targetStep) {
        targetStep.classList.add("active");
        targetStep.scrollIntoView({ behavior: "smooth" });
      } else {
        console.warn(`Step ${stepNumber} not found.`);
      }
    }

    displayCustomAlert(message) {
      if (this.customAlert && this.alertMessageContainer) {
        this.alertMessageContainer.innerHTML = `<b>${message}</b>`;
        this.alertMessageContainer.style.color = "#fd7ca5";
        this.customAlert.style.display = "block";
      } else {
        console.warn("Custom alert elements not found.");
      }
    }
  }

  // Model Class
  class PhoneCaseBuilderModel {
    constructor() {}
  }

  // View Class
  class PhoneCaseBuilderView {
    constructor() {}
  }

  // Controller Class
  class PhoneCaseBuilderController {
    constructor(model, view) {
      this.model = model;
      this.view = view;

      this.init();
    }

    init() {}
  }
  class PricingStructure {
    constructor(
      backProduct,
      backingsProducts,
      backComparePriceUSD,
      backComparePriceCAD,
      backingComparePriceUSD,
      backingComparePriceCAD,
      usdNoBackPrices,
      cadNoBackPrices,
      kits
    ) {
      this.backProduct = backProduct;
      this.backingsProducts = backingsProducts;
      this.backComparePriceUSD = parseFloat(backComparePriceUSD);
      this.backComparePriceCAD = parseFloat(backComparePriceCAD);
      this.backingComparePriceUSD = parseFloat(backingComparePriceUSD);
      this.backingComparePriceCAD = parseFloat(backingComparePriceCAD);
      this.usdNoBackPrices = usdNoBackPrices;
      this.cadNoBackPrices = cadNoBackPrices;
      this.kits = kits;
      this.pricingTiers = this.createPricingTiers();
      this.isCanada = window.Shopify && window.Shopify.country === "CA";
    }

    createPricingTiers() {
      const noBackPricesUSD = this.usdNoBackPrices.reduce((acc, price, index) => {
        const quantity = index + 1;
        acc[quantity] = {
          pricePerItem: parseFloat(price) / quantity,
          total: parseFloat(price),
          comparePrice: parseFloat(this.backingComparePriceUSD) * quantity,
          backPrice: 0,
        };
        return acc;
      }, {});

      const noBackPricesCAD = this.cadNoBackPrices.reduce((acc, price, index) => {
        const quantity = index + 1;
        acc[quantity] = {
          pricePerItem: parseFloat(price) / quantity,
          total: parseFloat(price),
          comparePrice: parseFloat(this.backingComparePriceCAD) * quantity,
          backPrice: 0,
        };
        return acc;
      }, {});

      const kitPricesUSD = this.kits.reduce((acc, kit) => {
        acc[kit.quantity] = {
          pricePerItem: parseFloat(kit.usdPrice) / kit.quantity,
          total: parseFloat(kit.usdPrice),
          comparePrice: parseFloat(this.backingComparePriceUSD) * kit.quantity,
          backPrice: parseFloat(this.backProduct.price) || 14,
        };
        return acc;
      }, {});

      const kitPricesCAD = this.kits.reduce((acc, kit) => {
        acc[kit.quantity] = {
          pricePerItem: parseFloat(kit.cadPrice) / kit.quantity,
          total: parseFloat(kit.cadPrice),
          comparePrice: parseFloat(this.backingComparePriceCAD) * kit.quantity,
          backPrice: parseFloat(this.backProduct.price) || 14,
        };
        return acc;
      }, {});

      return {
        noBackPrices: {
          usd: noBackPricesUSD,
          cad: noBackPricesCAD,
        },
        kitPrices: {
          usd: kitPricesUSD,
          cad: kitPricesCAD,
        },
      };
    }

    getNoBackBackingPrice(quantity, isCanada) {
      if (isCanada) {
        return (
          this.pricingTiers.noBackPrices.cad[quantity] || {
            pricePerItem: 41 / 10,
            total: 41,
            comparePrice: 10 * 10,
            backPrice: 0,
          }
        );
      } else {
        return (
          this.pricingTiers.noBackPrices.usd[quantity] || {
            pricePerItem: 30 / 10,
            total: 30,
            comparePrice: 10 * 10,
            backPrice: 0,
          }
        );
      }
    }

    getKitBackingPrice(kitSize, isCanada) {
      if (isCanada) {
        return (
          this.pricingTiers.kitPrices.cad[kitSize] || {
            pricePerItem: 41 / 10,
            total: 41,
            comparePrice: this.backingPriceCAD * 10,
            backPrice: this.backPriceCAD,
          }
        );
      } else {
        return (
          this.pricingTiers.kitPrices.usd[kitSize] || {
            pricePerItem: 30 / 10,
            total: 30,
            comparePrice: this.backingPriceUSD * 10,
            backPrice: this.backPriceUSD,
          }
        );
      }
    }
  }

  class PriceManager {
    constructor(pricingStructure) {
      this.pricingStructure = pricingStructure;
    }

    calculateBackingsPrice(quantity, isNoOptionSelected, isCanada) {
      const pricingDetails = isNoOptionSelected
        ? this.pricingStructure.getNoBackBackingPrice(quantity, isCanada)
        : this.pricingStructure.getKitBackingPrice(quantity, isCanada);

      return {
        totalBackingPrice: pricingDetails.total || 0,
        compareBackingPrice: pricingDetails.comparePrice || 0,
        backPrice: pricingDetails.backPrice || 0,
      };
    }

    calculateTotalPrice(quantity, isNoOptionSelected, isCanada) {
      const { totalBackingPrice, compareBackingPrice, backPrice } = this.calculateBackingsPrice(
        quantity,
        isNoOptionSelected,
        isCanada
      );

      return {
        totalPrice: totalBackingPrice + backPrice,
        comparePrice: compareBackingPrice + backPrice,
      };
    }
  }
  if (
    window.backProduct &&
    window.backingsProducts &&
    window.backComparePriceUSD &&
    window.backComparePriceCAD &&
    window.backingComparePriceUSD &&
    window.backingComparePriceCAD &&
    window.usdNoBackPrices &&
    window.cadNoBackPrices &&
    window.kits
  ) {
    const pricingStructure = new PricingStructure(
      window.backProduct,
      window.backingsProducts,
      window.backComparePriceUSD,
      window.backComparePriceCAD,
      window.backingComparePriceUSD,
      window.backingComparePriceCAD,
      window.usdNoBackPrices,
      window.cadNoBackPrices,
      window.kits
    );
    const priceManager = new PriceManager(pricingStructure);
    const phoneCaseBuilder = new PhoneCaseBuilder();
  }
});
